import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Input,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BreadCrumb from '../../../../Components/Common/BreadCrumb';
import DeleteModal from '../../../../Components/Common/DeleteModal';
import PlanningCreateModal from './PlanningCreateModal';
import * as api from './planningApi';
import { STATUS, STATUS_BADGE, HARD_RECALL_FILTERS } from './constants';
import { formatDateTime } from '../formatters';
import { exportPlanningCsv } from '../planningExport';

const headerLabel = label => (
  <span className='text-uppercase fw-semibold fs-13 mb-0 text-white'>
    {label}
  </span>
);

// UI column sortKey → PascalCase, whitelisted API sortField (contract section 2).
// Only these fields are accepted by the server; anything else 400s, so every
// sortable column below maps to one of them.
const SORT_FIELD_MAP = {
  procId: 'ProcurementCode',
  title: 'Title',
  procurementType: 'JenisPengadaanName',
  procurementMethod: 'MekanismePengadaanName',
  contractType: 'ContractType',
  description: 'Description',
  mainStatus: 'PhaseName',
  status: 'PhaseStatusName',
  createdDate: 'Created',
  updatedDate: 'Modified',
};

// The whitelist also accepts `IsHardRecall`, but recall state is rendered as a
// badge inside the Status Planning column rather than as a column of its own, so
// there is no header to hang that sort on. The `isHardRecall` filter below
// covers the same need directly.

const PlanningView = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalPlanning: 0,
    draft: 0,
    submitted: 0,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState(null); // UI sortKey
  const [sortDirection, setSortDirection] = useState('asc');
  // null = no filter | true = recalled only | false = exclude recalled.
  const [hardRecallFilter, setHardRecallFilter] = useState(null);

  const [createModal, setCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  document.title = 'Planning';

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortField, sortDirection, hardRecallFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { items: rows, totalCount } = await api.listPlanning({
        page: currentPage,
        pageSize: rowsPerPage,
        searchText: debouncedSearch,
        sortField: sortField ? SORT_FIELD_MAP[sortField] : null,
        sortOrder: sortDirection === 'desc' ? 'Desc' : 'Asc',
        isHardRecall: hardRecallFilter,
      });
      setItems(rows);
      setTotalRows(totalCount);
    } catch (error) {
      toast.error(`Failed to load planning list: ${error}`);
      setItems([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearch,
    currentPage,
    rowsPerPage,
    sortField,
    sortDirection,
    hardRecallFilter,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Summary is unfiltered (counts all Planning), so it only needs refetching
  // after a create/delete changes the total — not on search/sort/page changes.
  const fetchSummary = useCallback(async () => {
    try {
      setSummary(await api.getPlanningSummary());
    } catch (error) {
      // Non-blocking: the list still works if the summary card fails.
      toast.error(`Failed to load planning summary: ${error}`);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // ---- Create --------------------------------------------------------------
  const handleCreate = async payload => {
    setCreating(true);
    try {
      const res = await api.createPlanning(payload);
      toast.success(`Planning ${res.procurementCode} created`);
      setCreateModal(false);
      // planningId (NOT procurementId) is the {planningId} used by every route.
      navigate(`/crude/planning/create/${res.planningId}`);
    } catch (error) {
      toast.error(`Failed to create planning: ${error}`);
    } finally {
      setCreating(false);
    }
  };

  // ---- Delete --------------------------------------------------------------
  const askDelete = row => {
    setSelectedRow(row);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (selectedRow) {
      try {
        await api.deletePlanning(selectedRow.id);
        toast.success('Planning has been deleted');
      } catch (error) {
        toast.error(`Failed to delete planning: ${error}`);
      }
    }
    setDeleteModal(false);
    setSelectedRow(null);
    fetchData();
    fetchSummary();
  };

  // ---- Export --------------------------------------------------------------
  // Excel is server-generated (whole filtered result); CSV stays a client-side
  // dump of the current page (no server CSV endpoint in the contract).
  const handleExportExcel = async () => {
    try {
      const blob = await api.downloadPlanningExcel({
        searchText: debouncedSearch,
        sortField: sortField ? SORT_FIELD_MAP[sortField] : null,
        sortOrder: sortDirection === 'desc' ? 'Desc' : 'Asc',
        isHardRecall: hardRecallFilter,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PlanningList_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Export failed: ${error}`);
    }
  };
  const handleExportCsv = () => {
    if (!items.length) return toast.info('No data to export');
    exportPlanningCsv(items);
  };

  const handleSort = (column, direction) => {
    setSortField(column.sortKey || null);
    setSortDirection(direction);
  };

  // Each column declares a minWidth sized to its content. Without one, RDT
  // divides the container evenly and every column ends up cramped; with them
  // the table grows past the container and scrolls horizontally instead.
  // Susunan kolom mengikuti desain acuan: ID | Created | Updated | Title |
  // Procurement Type (= mekanisme, mis. "Pelelangan Khusus") | Period (Term/
  // Spot) | Type (komoditas, badge) | Description | Status | Action.
  const columns = [
    {
      name: headerLabel('Id Procurement'),
      sortKey: 'procId',
      sortable: true,
      minWidth: '190px',
      selector: row => row.procId,
      cell: row => (
        <span className='fw-semibold font-monospace fs-13'>{row.procId}</span>
      ),
    },
    {
      name: headerLabel('Created Date'),
      sortKey: 'createdDate',
      sortable: true,
      minWidth: '165px',
      selector: row => row.createdDate,
      cell: row => (
        <span className='text-muted fs-13'>
          {formatDateTime(row.createdDate)}
        </span>
      ),
    },
    {
      name: headerLabel('Updated Date'),
      sortKey: 'updatedDate',
      sortable: true,
      minWidth: '165px',
      selector: row => row.updatedDate,
      // Amber tint mengikuti desain acuan — menandai kolom sort default.
      style: { backgroundColor: '#fffbea' },
      // `Modified` null selama baris belum pernah diedit — fallback ke Created
      // (mengikuti desain acuan) supaya kolom tidak pernah kosong.
      cell: row => (
        <span className='fw-medium fs-13'>
          {formatDateTime(row.updatedDate || row.createdDate)}
        </span>
      ),
    },
    {
      name: headerLabel('Title'),
      sortKey: 'title',
      sortable: true,
      wrap: true,
      minWidth: '220px',
      selector: row => row.title,
      cell: row => <span className='fw-semibold'>{row.title}</span>,
    },
    {
      name: headerLabel('Procurement Type'),
      sortKey: 'procurementMethod',
      sortable: true,
      wrap: true,
      minWidth: '190px',
      selector: row => row.procurementMethod,
    },
    {
      name: headerLabel('Period'),
      sortKey: 'contractType',
      sortable: true,
      center: true,
      minWidth: '110px',
      selector: row => row.contractType,
      cell: row => (
        <span className='fw-semibold fs-13'>
          {row.contractType ? row.contractType.toUpperCase() : '-'}
        </span>
      ),
    },
    {
      name: headerLabel('Type'),
      sortKey: 'procurementType',
      sortable: true,
      center: true,
      minWidth: '190px',
      selector: row => row.procurementType,
      cell: row => (
        <span className='badge border border-primary text-primary bg-primary-subtle rounded-pill px-2'>
          {row.procurementType}
        </span>
      ),
    },
    {
      name: headerLabel('Description'),
      sortKey: 'description',
      sortable: true,
      wrap: true,
      minWidth: '200px',
      selector: row => row.description,
      cell: row => row.description || '-',
    },
    {
      name: headerLabel('Status'),
      sortKey: 'status',
      sortable: true,
      center: true,
      minWidth: '190px',
      selector: row => row.status,
      cell: row => {
        // Status PLANNING (bukan status fase header): begitu fase header sudah
        // melewati 'Planning' (mis. 'Procurement' yang mulai lagi sebagai
        // Draft), Planning ini sudah SUBMITTED — tanpa turunan ini, baris yang
        // baru di-submit tampak "masih Draft".
        const planningStatus =
          row.mainStatus && row.mainStatus !== 'Planning'
            ? STATUS.SUBMITTED
            : row.status;
        return (
          <div className='d-flex flex-column align-items-center gap-1'>
            <Badge
              color={STATUS_BADGE[planningStatus] || 'secondary'}
              className='px-2'
            >
              {planningStatus}
            </Badge>
            {/* Hard Recall is not a PhaseStatus of its own — the row still reads
              'Draft'. Without this marker a user cannot tell why Delete is
              disabled on one Draft row and not another. */}
            {row.isHardRecall && (
              <Badge
                color='danger'
                className='px-2'
                title='Returned for revision'
              >
                Hard Recall
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      name: headerLabel('Action'),
      minWidth: '160px', // View + Edit + Delete side by side on a Draft row
      center: true,
      cell: row => {
        // Edit is offered for the two editable states: plain Draft, and a row
        // reopened by Hard Recall. `isHardRecall` is checked in its own right
        // rather than inferred from the status text, because the contract does
        // not pin down whether `phaseStatusName` here is Planning's own status
        // (back to 'Draft') or the Procurement header's (which can still read
        // 'On Hard Recall'). Keying off the flag makes the row actionable either
        // way — otherwise a recalled row could render no edit affordance at all.
        // Draft hanya bisa diedit selama fase header masih 'Planning' — begitu
        // fase maju (Procurement dst.), phaseStatus 'Draft' milik fase BARU,
        // bukan Planning; tanpa syarat mainStatus, baris yang sudah submitted
        // memunculkan tombol Edit yang pasti ditolak server.
        const canEdit =
          (row.mainStatus === 'Planning' &&
            (row.status === STATUS.DRAFT ||
              row.status === STATUS.HARD_RECALL)) ||
          row.isHardRecall;
        return (
          <div className='d-flex gap-1 justify-content-center'>
            {/* View is unconditional — the read-only Detail is valid at every
                status. Edit and Delete are the conditional ones below. */}
            <button
              className='btn btn-soft-secondary btn-sm btn-icon'
              title='View Detail'
              onClick={() => navigate(`/crude/planning/dtl/${row.id}`)}
            >
              <i className='ri-eye-line align-bottom'></i>
            </button>
            {canEdit && (
              <>
                <button
                  className='btn btn-soft-info btn-sm btn-icon'
                  title='Edit'
                  onClick={() => navigate(`/crude/planning/create/${row.id}`)}
                >
                  <i className='ri-pencil-fill align-bottom'></i>
                </button>
                {/* DeletePlanning 422s during a Hard Recall ("...sedang dalam
                    proses Hard Recall...") because the cascade would take the
                    running ProcurementValidity/TenderOpening with it (contract
                    section 5). Disabled-with-reason rather than hidden, so the
                    difference from a plain Draft row is visible. Editing stays
                    available — the other 17 mutation endpoints do reopen. */}
                <button
                  className='btn btn-soft-danger btn-sm btn-icon'
                  title={
                    row.isHardRecall
                      ? 'Cannot delete while a Hard Recall is in progress'
                      : 'Delete'
                  }
                  disabled={row.isHardRecall}
                  onClick={() => askDelete(row)}
                >
                  <i className='ri-delete-bin-6-line align-bottom'></i>
                </button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <PlanningCreateModal
          show={createModal}
          submitting={creating}
          onClose={() => setCreateModal(false)}
          onSave={handleCreate}
        />
        <Container fluid>
          <BreadCrumb title='Planning' pageTitle='Plan & Procurement' />

          {/* ---- Summary cards: counts across ALL planning (unfiltered) ---- */}
          <Row className='g-3 mb-2'>
            {[
              {
                label: 'Total Planning',
                value: summary.totalPlanning,
                icon: 'ri-stack-line',
                color: 'primary',
              },
              {
                label: 'Draft',
                value: summary.draft,
                icon: 'ri-draft-line',
                color: 'warning',
              },
              {
                label: 'Submitted',
                value: summary.submitted,
                icon: 'ri-checkbox-circle-line',
                color: 'success',
              },
            ].map(c => (
              <Col xl={4} md={6} key={c.label}>
                <Card
                  className={`card-animate border shadow-none bg-soft-${c.color}`}
                >
                  <CardBody className='py-3'>
                    <div className='d-flex align-items-center'>
                      <div className='flex-shrink-0 me-3'>
                        <i className={`${c.icon} fs-1 text-${c.color}`}></i>
                      </div>
                      <div>
                        <p className='text-muted mb-1 fw-semibold text-uppercase fs-12'>
                          {c.label}
                        </p>
                        <h4 className='mb-0'>{c.value}</h4>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          <Row>
            <Col lg={12}>
              <Card id='PlanningList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      Planning List
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <div className='search-box'>
                          <Input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Search Title / Id / Description...'
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                          />
                        </div>
                        {/* Server-side isHardRecall filter (section 2). The
                            option values are indices, not the booleans
                            themselves: a select coerces its value to a string,
                            which would turn `null` into "null" and `false` into
                            a truthy "false". */}
                        <Input
                          type='select'
                          className='form-select form-select-sm'
                          style={{ width: 'auto' }}
                          aria-label='Filter by Hard Recall'
                          value={String(
                            HARD_RECALL_FILTERS.findIndex(
                              o => o.value === hardRecallFilter
                            )
                          )}
                          onChange={e =>
                            setHardRecallFilter(
                              HARD_RECALL_FILTERS[Number(e.target.value)].value
                            )
                          }
                        >
                          {HARD_RECALL_FILTERS.map((o, i) => (
                            <option key={o.label} value={String(i)}>
                              {o.label}
                            </option>
                          ))}
                        </Input>
                        <UncontrolledDropdown>
                          <DropdownToggle
                            tag='button'
                            className='btn btn-soft-success btn-sm'
                          >
                            <i className='ri-download-2-line align-bottom me-1'></i>
                            Export
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem onClick={handleExportExcel}>
                              <i className='ri-file-excel-2-line align-bottom me-2 text-success'></i>
                              Export Excel
                            </DropdownItem>
                            <DropdownItem onClick={handleExportCsv}>
                              <i className='ri-file-text-line align-bottom me-2 text-secondary'></i>
                              Export CSV
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                        <button
                          type='button'
                          className='btn btn-primary btn-label right ms-auto nexttab btn-sm'
                          onClick={() => setCreateModal(true)}
                        >
                          <i className='ri-add-line label-icon align-middle fs-16 ms-2'></i>{' '}
                          <span className='d-none d-sm-inline'>Create New</span>
                          <span className='d-sm-none'>New</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div className='table-responsive'>
                    <DataTable
                      columns={columns}
                      data={items}
                      highlightOnHover
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={rowsPerPage}
                      paginationDefaultPage={currentPage}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={page => setCurrentPage(page)}
                      onChangeRowsPerPage={newPerPage => {
                        setRowsPerPage(newPerPage);
                        setCurrentPage(1);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No planning found
                        </div>
                      }
                      customStyles={{
                        headCells: {
                          style: {
                            // Navy gelap + teks putih mengikuti desain acuan.
                            backgroundColor: '#1e3a56',
                            color: '#ffffff',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid rgba(255,255,255,0.15)',
                            // Headers stay on one line — they set the column's
                            // real floor, so a wrapped header would let the
                            // column collapse narrower than its own label.
                            whiteSpace: 'nowrap',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: { style: { minHeight: '50px' } },
                      }}
                    />
                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PlanningView;
